<script setup lang="ts">
import {
  getPurchaseEligibility,
  PURCHASE_UNAVAILABLE_REASON_LOW_VALUE,
} from "~/utils/purchase-eligibility.utils";

const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  filterParams: {
    type: String,
    required: false,
  },
});

const { trackEvent } = useGAnalytics();
const { t } = useI18n();
const route = useRoute();
const userStore = useUserStore();
const GFALExStore = useGFALExStore();
const { marketplaceFeesPercent, minimumPrice } = useRuntimeConfig().public;
const isModalBuyShown = ref<boolean>(false);
const isModalOfferMakeShown = ref<boolean>(false);
const showBuyUnavailableTooltip = ref<boolean>(false);

const { hideRecommendedPrice } = useRuntimeConfig().public;
const { getMerchantAttributes } = useLuxuryDesign();
const collectibleDesign = computed(() =>
  getMerchantAttributes(props.collectible.merchantId)
);

const feeCoverageEligibility = computed(() => {
  return getPurchaseEligibility(
    {
      priceGFAL: props.collectible.price?.GFAL,
      isPurchasable: props.collectible.isPurchasable,
      purchaseBlockReason: props.collectible.purchaseBlockReason,
    },
    {
      marketplaceFeesPercent: Number(marketplaceFeesPercent),
      minimumPriceUSD: Number(minimumPrice),
      gfalExRate: Number(GFALExStore.GFALExRate),
    }
  );
});

const isPurchaseUnavailableByFeeCoverage = computed(() => {
  return (
    !feeCoverageEligibility.value.isPurchasable &&
    feeCoverageEligibility.value.purchaseBlockReason ===
      PURCHASE_UNAVAILABLE_REASON_LOW_VALUE
  );
});

const isBuyDisabled = computed(
  () =>
    userStore.isCurrentUserId(props.collectible.gfalId) ||
    !props.collectible.price?.GFAL ||
    isPurchaseUnavailableByFeeCoverage.value
);

const openBuyModal = () => {
  if (isBuyDisabled.value) return;
  isModalBuyShown.value = true;
  trackEvent(`buy_${route.name as string}`);
};
</script>

<template>
  <div
    v-bind="collectibleDesign"
    class="grid grid-cols-4 sm:grid-cols-12 w-full border border-primary-900 rounded-md mb-6 merchant-card">
    <div
      class="border-r border-primary-900 h-[88px] overflow-hidden order-1 sm:order-none">
      <NuxtLinkLocale
        :to="`/market/${$route.params.merchant}/${collectible.id}?stackSetId=${$route.params.setId}`"
        @click="
          trackEvent(`click_collectible_${$route.name as string}`, {
            collectibleId: collectible.id,
          })
        "
        class="w-full h-full">
        <img
          class="object-cover rounded-l-md w-full h-full"
          :src="getPortraitItemImage(collectible.imageId)"
          @error="
            (e) => ((e.target as HTMLImageElement).src = getPortraitItemImage())
          " />
      </NuxtLinkLocale>
    </div>
    <div
      class="relative border-r border-primary-900 flex items-center justify-center order-2 sm:order-none collectible-card__level-container">
      <BaseIconHexagon class="w-12 h-12" />
      <p class="absolute text-neutral-200 text-2xl font-extrabold">
        {{ collectible.level }}
      </p>
    </div>
    <div
      class="hidden border-t sm:border-r sm:border-t-0 border-primary-900 col-span-1 sm:hidden xl:col-span-1 xl:flex items-center justify-center order-5 sm:order-none">
      <div class="flex items-baseline">
        <p
          class="bg-neutral-500 border border-neutral-400 text-neutral-200 font-bold text-center w-fit px-3 py-1 rounded-lg">
          #{{ collectible.number }}
        </p>
      </div>
    </div>
    <div
      class="hidden border-t border-r sm:border-t-0 border-primary-900 py-2 sm:py-0 col-span-3 sm:col-span-3 sm:hidden xl:flex justify-center items-center order-4 sm:order-none"
      :class="
        collectible.price?.GFAL || hideRecommendedPrice.toString() !== 'true'
          ? 'sm:col-span-3'
          : 'sm:col-span-6'
      ">
      <div class="group">
        <CommonUserCard
          :id="collectible.gfalId"
          size="custom"
          custom-class="w-9"
          :special-border="userStore.isCurrentUserId(collectible.gfalId)"
          :special-text="userStore.isCurrentUserId(collectible.gfalId)" />
      </div>
    </div>
    <div
      v-if="collectible.price || hideRecommendedPrice.toString() !== 'true'"
      class="sm:border-r border-primary-900 py-4 sm:py-0 col-span-2 sm:col-span-4 xl:col-span-3 flex justify-center items-center order-3 sm:order-none">
      <div class="pl-1 flex items-start sm:items-center gap-2">
        <BaseIcon
          name="GFAL_token"
          class="w-6"
          :alt="$t('main.imageAltTexts.GFALTokenLogo')" />
        <div
          v-if="
            collectible.price ||
            collectible.recommendedPrice !== null ||
            collectible.recommendedPrice !== undefined
          "
          class="flex flex-col sm:flex-row items-baseline gap-x-2 flex-wrap">
          <p
            v-if="
              collectible.price?.GFAL ||
              collectible.recommendedPrice.GFAL !== null ||
              collectible.recommendedPrice.GFAL !== undefined
            "
            class="text-lg text-neutral-200 font-bold">
            {{
              roundNum(
                collectible.price?.GFAL || collectible.recommendedPrice.GFAL
              )
            }}
          </p>
          <p
            v-if="
              collectible.price?.USD ||
              collectible.recommendedPrice.USD !== null ||
              collectible.recommendedPrice.USD !== undefined
            "
            class="text-neutral-300">
            ~ ${{
              roundNum(
                collectible.price?.USD || collectible.recommendedPrice.USD
              )
            }}
          </p>
        </div>
      </div>
    </div>
    <div
      class="px-3 border-t sm:border-t-0 border-primary-900 py-4 sm:py-0 col-span-4 sm:col-span-6 xl:col-span-3 order-6 sm:order-none flex items-center">
      <div
        class="grid grid-cols-5 justify-center items-center place-items-center gap-x-2 mx-auto">
        <div class="relative">
          <span
            :tabindex="isPurchaseUnavailableByFeeCoverage ? 0 : -1"
            @mouseenter="showBuyUnavailableTooltip = true"
            @mouseleave="showBuyUnavailableTooltip = false"
            @focusin="showBuyUnavailableTooltip = true"
            @focusout="showBuyUnavailableTooltip = false">
            <BaseButtonSubmit
              :disabled="isBuyDisabled"
              size="sm"
              class="min-w-12 min-h-6 sm:h-auto"
              :class="{ 'peer ': !userStore.isCurrentUserId(collectible.gfalId) }"
              @click="openBuyModal"
              :contextual-styles="true">
              <template #image-prefix>
                <NuxtIcon
                  name="mdi:cart"
                  class="w-4 h-4 merchant-cta-icon--submit" />
              </template>
            </BaseButtonSubmit>
          </span>
          <CommonTooltip :is-visible="showBuyUnavailableTooltip">
            <template #tooltip-text>
              <p class="text-xs text-black font-semibold text-center whitespace-normal break-words">
                {{
                  isPurchaseUnavailableByFeeCoverage
                    ? t("main.storeDetail.itemUnavailableBelowFees")
                    : $t("main.storeDetail.buy")
                }}
              </p>
            </template>
          </CommonTooltip>
        </div>
        <div class="relative">
          <BaseButtonCancel
            :disabled="userStore.isCurrentUserId(collectible.gfalId)"
            size="sm"
            class="min-w-12 min-h-6 sm:h-auto flex items-center justify-center"
            :class="{ 'peer ': !userStore.isCurrentUserId(collectible.gfalId) }"
            @click="
              (isModalOfferMakeShown = true), trackEvent('make_offer_stack')
            "
            :contextual-styles="true">
            <template #image-prefix>
              <BaseIcon name="tag" class="w-4 h-4 merchant-cta-icon--cancel" />
            </template>
          </BaseButtonCancel>
          <CommonTooltip>
            <template #tooltip-text>
              <p class="text-xs text-black font-semibold">
                {{ $t("main.collectibleDetail.makeOffer") }}
              </p>
            </template>
          </CommonTooltip>
        </div>
        <NuxtLinkLocale
          :to="`/market/${$route.params.merchant}/${collectible.id}?stackSetId=${$route.params.setId}`"
          @click="trackEvent(`${collectible.id}_button`)"
          class="col-span-3 w-full">
          <BaseButtonSubmit
            :text="$t('main.cardCollectible.viewCollectible')"
            size="sm"
            class="w-full text-nowrap"
            :contextual-styles="true" />
        </NuxtLinkLocale>
      </div>
    </div>
  </div>
  <ModalOfferMake
    v-if="isModalOfferMakeShown"
    :collectible="collectible"
    @close="isModalOfferMakeShown = false" />
  <ModalBuy
    v-if="isModalBuyShown"
    :item="collectible"
    @close="isModalBuyShown = false" />
</template>
